import { Route, Routes, useLocation } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useEffect, useContext, useState } from 'react';
import MainPage from './pages/MainPage/MainPage';
import ErrorPage from './pages/ErrorPage';
import FaqPage from './pages/FaqPage';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Identities } from '../model/store/providers/Identities';
import { Localization } from '../model/store/providers/Localization';
import { api } from '../model/api/api';
import LoginPage from './pages/LoginPage';
import { useAuth0 } from '@auth0/auth0-react';
import { LoadingIndicator } from './components/LoadingIndicator';
import { localSystems } from '../model/localSystems';
import { values } from '../util/values';
import { Referrer } from '../model/store/providers/Referrer';
import { AuthRedirect } from '../model/store/providers/AuthRedirect';
import { LocalSystemFilter } from '../model/store/providers/LocalSystemFilter';
import { auth0Domain, mfaScopes, rawTenantDomain, ritchieIdApiUrl, scopes } from '../config';
import { UserInfo } from '../model/store/providers/UserInfo';
import { MfaFactors, RawMfaFactor } from '../model/store/providers/MfaFactors';
import axios from 'axios';

function App() {
  const { isLoading, isAuthenticated, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const atLoginPage = useLocation().pathname === '/login';
  const [showSpinner, setShowSpinner] = useState(!atLoginPage);
  const [checkingSession, setCheckingSession] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const dispatch = useContext(Identities.Dispatch);
  const dispatchLocalization = useContext(Localization.Dispatch);
  const dispatchUserInfo = useContext(UserInfo.Dispatch);
  const dispatchMfaFactors = useContext(MfaFactors.Dispatch);
  const { translationsInitialized, language } = useContext(Localization.State);
  const { userInfoInitialized } = useContext(UserInfo.State);
  const { isAuthRedirect, redirectionComplete } = useContext(AuthRedirect.State);
  const { mfaInitialized } = useContext(MfaFactors.State);

  useEffect(() => {
    for (const localSystem of values(localSystems)) {
      new Image().src = localSystem.logo;
      new Image().src = localSystem.icon;
    }

    // Show the loading indicator for a minimum amount of time to prevent flashing
    setTimeout(() => {
      setShowSpinner(false);
    }, 1000);
  }, []);

  useEffect(() => void async function() {
    if (isLoading) return;

    try {
      // Check the user's session on Auth0
      await getAccessTokenSilently({ scope: scopes });
    }
    catch(error) {
      // The user does not have an existing session on Auth0
    }
    finally {
      setCheckingSession(false);
    }
  }(), [isLoading, getAccessTokenSilently]);

  useEffect(() => void async function() {
    if (!isAuthenticated) {
      setInitialized(true);
      return;
    }

    let identities = {};

    try {
      setInitialized(false);
      identities = await api.getIdentities();
    }
    catch (e) {
      console.error(e);
    }

    try {
      const idTokenClaims = await getIdTokenClaims({
        audience: ritchieIdApiUrl,
        scope: scopes
      });

      dispatchUserInfo({
        type: 'setUserInfo',
        email: idTokenClaims.email || '',
        emailVerified: idTokenClaims.email_verified || false
      });
    }
    catch(e) {
      console.error(e);
    }

    dispatch({ type: 'initialize', identities });
    setInitialized(true);
  }(), [isAuthenticated, dispatch, dispatchUserInfo, getIdTokenClaims]);

  // Initialize translations
  useEffect(() => void async function() {
    if (isAuthRedirect && !redirectionComplete) return;

    try {
      const translations = await api.getTranslations(language);

      dispatchLocalization({ type: 'setTranslations', translations });
    }
    catch(error) {
      dispatchLocalization({ type: 'useDefaultTranslations' });
    }
  }(), [language, dispatchLocalization, isAuthRedirect, redirectionComplete]);

  // Initialize MFA factors
  useEffect(() => void async function() {
    if (!isAuthenticated) return;

    try {
      const token = await getAccessTokenSilently({
        scope: mfaScopes,
        audience: `https://${rawTenantDomain}/mfa/`
      });

      const results = await axios.get<RawMfaFactor[]>(`https://${auth0Domain}/mfa/authenticators`, {
        headers: {
          authorization:`Bearer ${token}`,
          'content-type': 'application/json'
        }
      });

      dispatchMfaFactors({
        type: 'setMfaFactors',
        factors: results.data
      });

      dispatchMfaFactors({ type: 'initialized' });
    }
    catch(error) {
      toast.error('Your MFA factors could not be retrieved.  Please refresh the page and try again.')
    }
  }(), [isAuthenticated, getAccessTokenSilently, dispatchMfaFactors]);

  if (atLoginPage && isLoading) return null;
  if (!atLoginPage) {
    const spinner = <LoadingIndicator show />;

    if (showSpinner) return spinner;
    if (isLoading) return spinner;
    if (checkingSession) return spinner;
    if (!initialized) return spinner;
    if (!translationsInitialized) return spinner;
    if (!userInfoInitialized && isAuthenticated) return spinner;
    if (isAuthRedirect && !redirectionComplete) return spinner;
    if (!mfaInitialized && isAuthenticated) return spinner;
  };

  return <>
    <Toaster
      position="bottom-center"
      reverseOrder={false}
    />
    <Routes>
      <Route
        path="/"
        element={
          isAuthenticated ?
            <MainPage /> :
            <LoginPage />
        }
      />
      <Route
        path="/faq"
        element={<FaqPage />}
      />
      <Route
        path="/login"
        element={<LoginWithRedirect />}
      />

      {/* Should always be last in the list of routes */}
      <Route
        path="*"
        element={<ErrorPage />}
      />
    </Routes>
  </>;
}

function LoginWithRedirect() {
  const { referrerLabel, referrerUrl } = useContext(Referrer.State);
  const { enabledLocalSystems } = useContext(LocalSystemFilter.State);
  const { language } = useContext(Localization.State);
  const { loginWithRedirect } = useAuth0();
  const ui_locales = language || undefined;

  useEffect(() => {
    loginWithRedirect({
      appState: {
        referrerLabel,
        referrerUrl,
        enabledLocalSystems,
        language
      },
      ui_locales
    });
  }, [loginWithRedirect, referrerLabel, referrerUrl, enabledLocalSystems, language, ui_locales]);

  return (
    <LoadingIndicator show />
  );
};

export default App;